import React, { useId } from 'react';
import { AlertTriangle, CheckCircle, Info, XCircle } from 'lucide-react';
import useModal from '../../hooks/useModal';

/**
 * Reusable Confirmation Dialog component
 * Replaces window.confirm() with a styled modal dialog
 */
const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'warning', // 'warning', 'danger', 'success', 'info'
  loading = false
}) => {
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(isOpen, onClose);
  const titleId = useId();
  const descId = useId();

  if (!isOpen) return null;

  const variants = {
    warning: {
      icon: AlertTriangle,
      iconColor: 'text-yellow-600',
      iconBg: 'bg-yellow-100',
      buttonColor: 'bg-yellow-600 hover:bg-yellow-700'
    },
    danger: {
      icon: XCircle,
      iconColor: 'text-red-600',
      iconBg: 'bg-red-100',
      buttonColor: 'bg-red-600 hover:bg-red-700'
    },
    success: {
      icon: CheckCircle,
      iconColor: 'text-green-600',
      iconBg: 'bg-green-100',
      buttonColor: 'bg-green-600 hover:bg-green-700'
    },
    info: {
      icon: Info,
      iconColor: 'text-gray-600',
      iconBg: 'bg-gray-100',
      buttonColor: 'bg-gray-500 hover:bg-gray-600'
    }
  };

  const config = variants[variant] || variants.warning;
  const Icon = config.icon;

  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm();
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[9999]"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10" aria-hidden="true"></div>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Icon */}
          <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${config.iconBg} mb-4`}>
            <Icon className={`h-6 w-6 ${config.iconColor}`} />
          </div>

          {/* Title */}
          <h3 id={titleId} className="text-lg font-medium text-gray-900 text-center mb-2">
            {title}
          </h3>

          {/* Message */}
          <p id={descId} className="text-sm text-gray-600 text-center mb-6">
            {message}
          </p>

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading}
              className={`flex-1 text-white px-4 py-2 rounded-lg disabled:opacity-50 transition-colors ${config.buttonColor}`}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </div>
              ) : (
                confirmText
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
